import React, { useEffect } from 'react';

import { IntlProvider } from 'react-intl';

import { Text } from '@atlaskit/primitives/compiled';
import { ConsentBanner, initializeControls } from '@atlassian/browser-storage-controls';

type Props = {
	/** Set to true when rendering on the server. */
	isSSR?: boolean;
	/**
	 * SSR-only: callback used by the underlying ConsentBanner to read a cookie
	 * value (e.g. `atl-gdpr-consent`) from the incoming request so it can
	 * decide whether to render the banner during server-side rendering.
	 */
	ssrGetCookie?: (name: string) => string | undefined;
};

/**
 * Atlassian cookie consent banner wrapper for the public Status Page.
 *
 * Wraps the shared `ConsentBanner` from `@atlassian/browser-storage-controls`
 * with the required `react-intl` provider, and an ADS `Text` primitive so the
 * banner inherits the Atlassian typography tokens instead of falling back
 * to the browser's default font.
 */
export const CookieConsentBanner = ({ ssrGetCookie, isSSR = false }: Props): React.JSX.Element => {
	useEffect(() => {
		// initializeControls is a client-only side effect.
		initializeControls({
			product: 'jira service management status page',
			analyticsEnabled: false,
			webStorageEnabled: true,
		});
	}, []);

	return (
		<IntlProvider locale="en">
			<Text>
				<ConsentBanner
					product="jsm-statuspage"
					analyticsEnabled={false}
					localConsentMode
					ssrOptions={{
						isSSR,
						getCookie: ssrGetCookie,
					}}
				/>
			</Text>
		</IntlProvider>
	);
};
